import { useMutation } from '@urql/vue'
import {
  TranslateEntityTextFieldsDocument,
  TranslationMode,
  type TranslatableEntityType,
  type TranslateEntityTextFieldsInput,
  type TranslationResult,
} from '~/graphql/operations'

export type TranslateFieldsArgs = {
  entityType: TranslatableEntityType
  entityId: number
  sourceLanguageId: number
  targetLanguageId: number
  fields: string[]
  mode: TranslationMode
  sourceChannelId?: number | null
  targetChannelId?: number | null
  modelOverride?: string | null
}

export const useTranslateEntityFields = () => {
  const mutation = useMutation(TranslateEntityTextFieldsDocument)

  const loading = ref(false)
  const error = ref<Error | null>(null)
  const lastResult = ref<TranslationResult | null>(null)

  const translateFields = async (
    args: TranslateFieldsArgs,
  ): Promise<TranslationResult> => {
    loading.value = true
    error.value = null
    try {
      const input: TranslateEntityTextFieldsInput = {
        entityType: args.entityType,
        entityId: args.entityId,
        sourceLanguageId: args.sourceLanguageId,
        targetLanguageId: args.targetLanguageId,
        fields: args.fields,
        mode: args.mode,
      }
      if (args.sourceChannelId != null) input.sourceChannelId = args.sourceChannelId
      if (args.targetChannelId != null) input.targetChannelId = args.targetChannelId
      if (args.modelOverride) input.modelOverride = args.modelOverride

      const res = await mutation.executeMutation({ input })
      if (res.error) throw res.error
      const data = res.data?.translateEntityTextFields
      if (!data) throw new Error('No result returned from translation mutation')

      lastResult.value = data
      return data
    } catch (e: any) {
      const err = e instanceof Error ? e : new Error(String(e?.message || e))
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    translateFields,
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    lastResult: computed(() => lastResult.value),
    TranslationMode,
  }
}
